import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Snackbar, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Edit, Delete, Logout } from '@mui/icons-material';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from '../api';



const Category = () => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ title: '', slug: '' });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [togglingId, setTogglingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await apiGet('/admin/category');
      const data = res.data;
      if (data.status) setCategories(data.data.categories || []);
      else setSnackbar({ open: true, message: data.message || 'Failed to fetch categories', severity: 'error' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Server error', severity: 'error' });
    }
    setLoading(false);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const newItems = Array.from(categories);
    const [moved] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, moved);
    setCategories(newItems);
    try {
      const orderedIds = newItems.map(c => c._id);
      await apiPatch('/admin/category/reorder', { orderedIds });
      setSnackbar({ open: true, message: 'Order saved', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save order', severity: 'error' });
      fetchCategories();
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.slug.trim()) {
      setSnackbar({ open: true, message: 'Title and slug are required', severity: 'warning' });
      return;
    }
    setLoading(true);
    try {
      if (editingId) {
        // Update
        const res = await apiPut(`${'/admin/category'}/${editingId}`, form);
        const data = res.data;
        if (data.status) {
          setSnackbar({ open: true, message: 'Category updated', severity: 'success' });
          fetchCategories();
        } else {
          setSnackbar({ open: true, message: data.message || 'Update failed', severity: 'error' });
        }
        setEditingId(null);
      } else {
        // Add
        const res = await apiPost('/admin/category', form);
        const data = res.data;
        if (data.status) {
          setSnackbar({ open: true, message: 'Category added', severity: 'success' });
          fetchCategories();
        } else {
          setSnackbar({ open: true, message: data.message || 'Add failed', severity: 'error' });
        }
      }
      setForm({ title: '', slug: '' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Server error', severity: 'error' });
    }
    setLoading(false);
  };

  const handleEdit = (cat) => {
    setForm({ title: cat.title, slug: cat.slug });
    setEditingId(cat._id);
  };

  const handleDelete = async (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      const res = await apiDelete(`/admin/category/${deleteDialog.id}`);
      const data = res.data;
      if (data.status) {
        setSnackbar({ open: true, message: 'Category deleted', severity: 'info' });
        fetchCategories();
      } else {
        setSnackbar({ open: true, message: data.message || 'Delete failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Server error', severity: 'error' });
    }
    setLoading(false);
    setDeleteDialog({ open: false, id: null });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, id: null });
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.location.reload();
  };

  const handleReset = () => {
    setForm({ title: '', slug: '' });
    setEditingId(null);
  };

  return (
    <Box p={4}>
      
      <Paper sx={{ p: 2, mb: 4 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 16 }}>
          <TextField
            label="Title"
            name="title"
            value={form.title}
            onChange={handleChange}
            required
          />
          <TextField
            label="Slug"
            name="slug"
            value={form.slug}
            onChange={handleChange}
            required
          />
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {editingId ? 'Update' : 'Add'}
          </Button>
          {editingId && (
            <Button type="button" variant="outlined" color="secondary" onClick={handleReset} disabled={loading}>
              Reset
            </Button>
          )}
        </form>
      </Paper>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <DragDropContext onDragEnd={onDragEnd}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Banners</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <Droppable droppableId="cat-table">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {categories.map((cat, index) => (
                    <Draggable key={cat._id} draggableId={cat._id} index={index}>
                      {(dragProvided) => (
                        <TableRow ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
                  <TableCell>{cat.title}</TableCell>
                  <TableCell>{cat.slug}</TableCell>
                  <TableCell>{cat.bannerCount || 0}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={cat.isSuspended ? 'Inactive' : 'Active'}
                      color={cat.isSuspended ? 'default' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(cat)}><Edit /></IconButton>
                    <Button
                      size="small"
                      variant={cat.isSuspended ? 'contained' : 'outlined'}
                      color={cat.isSuspended ? 'success' : 'warning'}
                      sx={{ mr: 1, textTransform: 'none' }}
                      disabled={togglingId === cat._id}
                      onClick={async () => {
                        try {
                          setTogglingId(cat._id);
                          // active should equal desired post-toggle state: when currently suspended -> activate (true), otherwise deactivate (false)
                          const res = await apiPatch(`/admin/category/${cat._id}/toggle-status`, { active: cat.isSuspended });
                          const data = res.data;
                          if (data.status) {
                            setSnackbar({ open: true, message: data.message, severity: 'success' });
                            fetchCategories();
                          } else {
                            setSnackbar({ open: true, message: data.message || 'Action failed', severity: 'error' });
                          }
                        } catch (err) {
                          const msg = err?.response?.data?.message || 'Server error';
                          setSnackbar({ open: true, message: msg, severity: 'error' });
                        }
                        setTogglingId(null);
                      }}
                    >
                      {cat.isSuspended ? 'Activate' : 'Deactivate'}
                    </Button>
                    <IconButton onClick={() => handleDelete(cat._id)} color="error"><Delete /></IconButton>
                  </TableCell>
                </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </TableBody>
          </Table>
          </DragDropContext>
        </TableContainer>
      )}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this category? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Category; 